import { useState } from 'react';
import { useMutation, useApolloClient, gql } from '@apollo/client';
import { CREATE_REVIEW_SECURE } from '../../graphql/mutations/reviewsMutations';
import { trackReviewSubmitted } from '../../util/tracking';
import {
  createAlreadyReviewedError,
  normalizeReviewError,
} from './reviewErrorUtils';

const GET_BOOKING_DETAILS = gql`
  query GetBooking($id: ID!) {
    getBooking(id: $id) {
      id
      status
      guestReviewed
      hostReviewed
      reviews {
        items {
          id
          reviewerId
          revieweeId
          reviewType
          rating
          content
        }
      }
    }
  }
`;

export const useCreateHostStudioReviews = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const client = useApolloClient();

  const [createReviewSecureMutation] = useMutation(CREATE_REVIEW_SECURE);

  const checkIfAlreadyReviewed = async (reviewData) => {
    try {
      const { data } = await client.query({
        query: GET_BOOKING_DETAILS,
        variables: { id: reviewData.bookingId },
        fetchPolicy: 'network-only'
      });

      if (!data?.getBooking) {
        return false;
      }

      const { reviews, guestReviewed } = data.getBooking;

      if (guestReviewed) {
        return true;
      }

      // Check if there's already a HOST_REVIEW for this booking
      const hasHostReview = reviews?.items?.some(
        review => review?.reviewType === "HOST_REVIEW"
      );

      return hasHostReview;
    } catch (error) {
      return false;
    }
  };

  const invalidateRelevantCaches = async ({ bookingId }) => {
    try {
      if (bookingId) {
        await client.query({
          query: GET_BOOKING_DETAILS,
          variables: { id: bookingId },
          fetchPolicy: 'network-only'
        });
      }
      
      await client.reFetchObservableQueries();
    } catch (error) {
      console.error('[useCreateHostStudioReviews] Cache invalidation error:', error);
    }
  };

  const submitStudioReview = async (reviewData) => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      const { 
        rating, 
        content, 
        communicationRating, 
        bookingId
      } = reviewData;
      
      if (!bookingId || !rating) {
        throw new Error('Missing required fields');
      }
      
      const alreadyReviewed = await checkIfAlreadyReviewed(reviewData);

      if (alreadyReviewed) {
        throw createAlreadyReviewedError();
      }

      const response = await createReviewSecureMutation({
        variables: {
          input: {
            bookingID: bookingId,
            rating,
            content,
            communicationRating,
            reviewType: "HOST_REVIEW"
          }
        }
      });
      
      await invalidateRelevantCaches({
        bookingId
      });

      trackReviewSubmitted({
        reviewType: 'host_review',
        rating,
        source: 'booking_details'
      });
      
      return response.data?.markBookingReviewed?.review;
    } catch (err) {
      const normalizedError = normalizeReviewError(err);
      console.error('[useCreateHostStudioReviews] Error submitting review:', normalizedError);
      setError(normalizedError);
      throw normalizedError;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    submitStudioReview,
    isSubmitting,
    error,
    invalidateRelevantCaches,
    checkIfAlreadyReviewed
  };
};
